import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from './utils/api';

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [product, setProduct] = useState({
    product_name: '',
    product_code: '',
    type: 'Product',
    product_category: '',
    product_description: '',
    output_description: '',
    customer: '',
    cost_price: '',
    supply_price: '',
    supply_markup_percent: '',
    service_price: '',
    service_markup_percent: '',
    markup: '', // Legacy, kept for compatibility
    price: '', // Legacy, kept for compatibility
    sales_tax_rate: '20% VAT',
    nominal_code: '',
    sales_account_code: '',
    purchase_account_code: '',
    purchase_tax_rate: '',
    location: '',
    barcode: '',
    supplier_id: '',
    supplier_sku: '',
    reorder_level: 0,
    reorder_quantity: 0,
    photo_url: '',
  });

  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [productSuppliers, setProductSuppliers] = useState([]);
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [defaultMarkup, setDefaultMarkup] = useState(0);
  const [newSupplier, setNewSupplier] = useState({
    supplier_id: '',
    supplier_sku: '',
    cost_price: '',
    lead_time_days: '',
    is_preferred: false,
    notes: ''
  });

  useEffect(() => {
    if (!isNew) {
      fetchProduct();
      fetchProductSuppliers();
    }
    fetchCategories();
    fetchSuppliers();
    fetchDefaultMarkup();
  }, [id]);

  // Auto-calculate supply_price when cost_price or supply_markup changes
  useEffect(() => {
    const cost = parseFloat(product.cost_price) || 0;
    const supplyMarkup = parseFloat(product.supply_markup_percent) || 0;
    
    if (cost > 0) {
      const calculatedSupplyPrice = cost * (1 + supplyMarkup / 100);
      setProduct(prev => ({...prev, supply_price: calculatedSupplyPrice.toFixed(2)}));
    }
  }, [product.cost_price, product.supply_markup_percent]);

  // Auto-calculate service_price when supply_price or service_markup changes
  useEffect(() => {
    const supply = parseFloat(product.supply_price) || 0;
    const serviceMarkup = parseFloat(product.service_markup_percent) || 0;
    
    if (supply > 0) {
      const calculatedServicePrice = supply * (1 + serviceMarkup / 100);
      setProduct(prev => ({...prev, service_price: calculatedServicePrice.toFixed(2)}));
    }
  }, [product.supply_price, product.service_markup_percent]);

  const fetchDefaultMarkup = async () => {
    try {
      const response = await apiFetch('/api/company-settings');
      const data = await response.json();
      const supplyMarkup = parseFloat(data.default_supply_markup_percent) || parseFloat(data.default_markup_percent) || 40;
      const serviceMarkup = parseFloat(data.default_service_markup_percent) || 25;
      
      // Apply default markups to new products
      if (isNew) {
        setProduct(prev => ({
          ...prev, 
          supply_markup_percent: supplyMarkup.toString(),
          service_markup_percent: serviceMarkup.toString()
        }));
      }
    } catch (err) {
      console.error('Error fetching default markups:', err);
      // Fallback to defaults if company_settings not found
      if (isNew) {
        setProduct(prev => ({
          ...prev,
          supply_markup_percent: '40',
          service_markup_percent: '25'
        }));
      }
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await apiFetch('/api/suppliers');
      const data = await response.json();
      setSuppliers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    }
  };

  const fetchProductSuppliers = async () => {
    if (isNew) return;
    try {
      const response = await apiFetch(`/api/product-suppliers/product/${id}`);
      const data = await response.json();
      setProductSuppliers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching product suppliers:', err);
    }
  };

  const handleAddSupplier = async (e) => {
    e.preventDefault();
    try {
      const response = await apiFetch('/api/product-suppliers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: id,
          ...newSupplier,
          cost_price: parseFloat(newSupplier.cost_price) || null,
          lead_time_days: parseInt(newSupplier.lead_time_days) || null
        })
      });
      
      if (response.ok) {
        alert('Supplier added successfully!');
        setShowAddSupplierModal(false);
        setNewSupplier({
          supplier_id: '',
          supplier_sku: '',
          cost_price: '',
          lead_time_days: '',
          is_preferred: false,
          notes: ''
        });
        fetchProductSuppliers();
      }
    } catch (err) {
      console.error('Error adding supplier:', err);
      alert('Failed to add supplier');
    }
  };

  const handleDeleteSupplier = async (supplierId) => {
    if (!window.confirm('Remove this supplier from the product?')) return;
    
    try {
      const response = await apiFetch(`/api/product-suppliers/${supplierId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        alert('Supplier removed');
        fetchProductSuppliers();
      }
    } catch (err) {
      console.error('Error deleting supplier:', err);
      alert('Failed to remove supplier');
    }
  };

  const handleSetPreferred = async (supplierId) => {
    try {
      const supplier = productSuppliers.find(ps => ps.id === supplierId);
      const response = await apiFetch(`/api/product-suppliers/${supplierId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...supplier,
          is_preferred: true
        })
      });
      
      if (response.ok) {
        fetchProductSuppliers();
      }
    } catch (err) {
      console.error('Error setting preferred supplier:', err);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiFetch('/api/custom-lists?list_type=parts_category');
      const data = await response.json();
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const fetchProduct = async () => {
    try {
      const response = await apiFetch(`/api/products/${id}`);
      const data = await response.json();
      setProduct(data);
      if (data.photo_url) {
        setPhotoPreview(data.photo_url);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const saveProduct = async () => {
    if (!product.product_name || !product.product_code || !product.supply_price) {
      alert('Product Name, Product Code, and Supply Price are required');
      return;
    }

    setSaving(true);
    try {
      // Prepare product data
      const productData = {
        ...product,
        cost_price: parseFloat(product.cost_price || 0),
        supply_price: parseFloat(product.supply_price || 0),
        supply_markup_percent: parseFloat(product.supply_markup_percent || 40),
        service_price: parseFloat(product.service_price || 0),
        service_markup_percent: parseFloat(product.service_markup_percent || 25),
        reorder_level: parseInt(product.reorder_level || 0),
        reorder_quantity: parseInt(product.reorder_quantity || 0),
      };

      // If photo file selected, convert to base64
      if (photoFile) {
        const reader = new FileReader();
        reader.onloadend = async () => {
          productData.photo_url = reader.result;
          await submitProduct(productData);
        };
        reader.readAsDataURL(photoFile);
      } else {
        await submitProduct(productData);
      }
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Failed to save product');
      setSaving(false);
    }
  };

  const submitProduct = async (productData) => {
    const url = isNew 
      ? '/api/products'
      : `/api/products/${id}`;
    
    const method = isNew ? 'POST' : 'PUT';

    const response = await apiFetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(productData),
    });

    if (response.ok) {
      // Only navigate away if creating a new product
      if (isNew) {
        const data = await response.json();
        const typeRoute = product.type === 'Service' ? '/services' : 
                         product.type === 'Consumable' ? '/consumables' : '/products';
        navigate(typeRoute);
      } else {
        // Stay on page when editing existing product
        alert('Product saved successfully');
      }
    } else {
      alert('Failed to save product');
    }
    setSaving(false);
  };

  const deleteProduct = async () => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;

    try {
      await apiFetch(`/api/products/${id}`, {
        method: 'DELETE',
      });
      const typeRoute = product.type === 'Service' ? '/services' : 
                       product.type === 'Consumable' ? '/consumables' : '/products';
      navigate(typeRoute);
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Failed to delete product');
    }
  };



  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
        <div className="max-w-full mx-auto px-6 h-full flex flex-col justify-center">
          <div className="flex items-center justify-between">
            <button
              onClick={() => {
                const typeRoute = product.type === 'Service' ? '/labour-costs' : 
                                 product.type === 'Consumable' ? '/consumables' : '/products';
                navigate(typeRoute);
              }}
              className="text-white hover:text-blue-100 transition text-lg font-semibold"
            >
              ⬅ Back
            </button>
            <div className="flex-1 text-center">
              <h1 className="text-4xl font-bold">
                {isNew ? `New ${product.type}` : product.product_name}
              </h1>
              {product.product_code && (
                <p className="text-sm text-blue-100 mt-1">Code: {product.product_code}</p>
              )}
            </div>
            <div className="flex gap-2">
              {!isNew && (
                <button
                  onClick={deleteProduct}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition text-sm shadow-sm"
                >
                  🗑️ Delete
                </button>
              )}
              <button
                onClick={saveProduct}
                disabled={saving}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm disabled:opacity-50"
              >
                {saving ? '⏳ Saving...' : '💾 Save'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-8">

      <div className="grid grid-cols-3 gap-6">
        {/* Main Content - 2/3 width */}
        <div className="col-span-2 space-y-6">
          
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={product.product_name}
                  onChange={(e) => setProduct(prev => ({ ...prev, product_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  maxLength={100}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={product.product_code}
                  onChange={(e) => setProduct(prev => ({ ...prev, product_code: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={product.type}
                  onChange={(e) => setProduct(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Product">Product</option>
                  <option value="Service">Service</option>
                  <option value="Consumable">Consumable</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={product.product_category}
                  onChange={(e) => setProduct(prev => ({ ...prev, product_category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- Select Category --</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.list_value}>{cat.list_value}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Manage categories in <a href="/settings" className="text-blue-600 hover:underline">Settings → Parts Categories</a>
                </p>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={product.product_description}
                  onChange={(e) => setProduct(prev => ({ ...prev, product_description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  maxLength={100}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Output Description</label>
                <textarea
                  value={product.output_description || ''}
                  onChange={(e) => setProduct(prev => ({ ...prev, output_description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  maxLength={500}
                  placeholder="Description for quotes, invoices, and other documents"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Barcode</label>
                <input
                  type="text"
                  value={product.barcode}
                  onChange={(e) => setProduct(prev => ({ ...prev, barcode: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stock Location</label>
                <input
                  type="text"
                  value={product.location}
                  onChange={(e) => setProduct(prev => ({ ...prev, location: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  maxLength={100}
                />
              </div>
            </div>
          </div>

          {/* Dual Pricing */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Dual Pricing System</h2>
            <p className="text-sm text-gray-600 mb-4">
              Cost → Supply Price (Sales Orders) → Service Price (Jobs)
            </p>

            {/* Cost Price */}
            <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-gray-900 mb-2">Cost Price (Base)</h3>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cost Price <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">£</span>
                    <input
                      type="number"
                      step="0.01"
                      value={product.cost_price}
                      onChange={(e) => setProduct(prev => ({ ...prev, cost_price: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">What you pay suppliers</p>
                </div>
              </div>
            </div>

            {/* Tier 1: Supply Price */}
            <div className="bg-blue-50 border border-blue-300 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-blue-900 mb-2">Tier 1: Supply Price (Sales Orders)</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Markup %
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={product.supply_markup_percent}
                    onChange={(e) => setProduct(prev => ({ ...prev, supply_markup_percent: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="40"
                  />
                  <p className="text-xs text-gray-500 mt-1">Default: 40%</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Supply Price <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">£</span>
                    <input
                      type="number"
                      step="0.01"
                      value={product.supply_price}
                      onChange={(e) => setProduct(prev => ({ ...prev, supply_price: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-semibold"
                      placeholder="0.00"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">For supply-only customers</p>
                </div>
              </div>
            </div>

            {/* Tier 2: Service Price */}
            <div className="bg-green-50 border border-green-300 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-green-900 mb-2">Tier 2: Service Price (Jobs)</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Markup % (on Supply Price)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={product.service_markup_percent}
                    onChange={(e) => setProduct(prev => ({ ...prev, service_markup_percent: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="25"
                  />
                  <p className="text-xs text-gray-500 mt-1">Default: 25%</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Price
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">£</span>
                    <input
                      type="number"
                      step="0.01"
                      value={product.service_price}
                      onChange={(e) => setProduct(prev => ({ ...prev, service_price: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-semibold"
                      placeholder="0.00"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">For service jobs</p>
                </div>
              </div>
            </div>

            {/* Pricing Summary */}
            {product.cost_price && product.supply_price && product.service_price && (
              <div className="bg-white border-2 border-gray-300 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Pricing Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cost Price:</span>
                    <span className="font-medium">£{parseFloat(product.cost_price).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Supply Price (Sales Orders):</span>
                    <span className="font-semibold text-blue-700">£{parseFloat(product.supply_price).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service Price (Jobs):</span>
                    <span className="font-semibold text-green-700">£{parseFloat(product.service_price).toFixed(2)}</span>
                  </div>
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Supply Margin:</span>
                      <span className="font-medium text-blue-700">
                        £{(parseFloat(product.supply_price) - parseFloat(product.cost_price)).toFixed(2)} 
                        ({product.supply_markup_percent}%)
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Service Margin (total):</span>
                      <span className="font-medium text-green-700">
                        £{(parseFloat(product.service_price) - parseFloat(product.cost_price)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tax Rates */}
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sales Tax Rate <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={product.sales_tax_rate}
                  onChange={(e) => setProduct(prev => ({ ...prev, sales_tax_rate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  maxLength={100}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Tax Rate</label>
                <input
                  type="text"
                  value={product.purchase_tax_rate}
                  onChange={(e) => setProduct(prev => ({ ...prev, purchase_tax_rate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  maxLength={100}
                />
              </div>
            </div>
          </div>

          {/* Accounting */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Accounting Codes</h2>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nominal Code</label>
                <input
                  type="text"
                  value={product.nominal_code}
                  onChange={(e) => setProduct(prev => ({ ...prev, nominal_code: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  maxLength={25}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sales Account Code</label>
                <input
                  type="text"
                  value={product.sales_account_code}
                  onChange={(e) => setProduct(prev => ({ ...prev, sales_account_code: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  maxLength={203}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Account Code</label>
                <input
                  type="text"
                  value={product.purchase_account_code}
                  onChange={(e) => setProduct(prev => ({ ...prev, purchase_account_code: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  maxLength={203}
                />
              </div>
            </div>
          </div>

          {/* Suppliers (Multiple) */}
          {product.type === 'Product' && !isNew && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Suppliers</h2>
                <button
                  onClick={() => setShowAddSupplierModal(true)}
                  className="px-3 py-1 bg-gray-300 hover:bg-gray-400 text-black rounded text-sm font-medium"
                >
                  ➕ Add Supplier
                </button>
              </div>

              {productSuppliers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No suppliers configured</p>
                  <p className="text-sm mt-1">Add suppliers to track ordering sources</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {productSuppliers.map(ps => (
                    <div key={ps.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-gray-900">{ps.supplier_name}</h3>
                            {ps.is_preferred && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
                                Preferred
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                            <div>
                              <span className="font-medium">Supplier SKU:</span> {ps.supplier_sku || '—'}
                            </div>
                            <div>
                              <span className="font-medium">Cost:</span> £{parseFloat(ps.cost_price || 0).toFixed(2)}
                            </div>
                            <div>
                              <span className="font-medium">Lead Time:</span> {ps.lead_time_days || '—'} days
                            </div>
                            <div>
                              <span className="font-medium">Contact:</span> {ps.contact_name || '—'}
                            </div>
                          </div>
                          {ps.notes && (
                            <div className="text-xs text-gray-500 mt-2">{ps.notes}</div>
                          )}
                        </div>
                        <div className="flex gap-2 ml-4">
                          {!ps.is_preferred && (
                            <button
                              onClick={() => handleSetPreferred(ps.id)}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                              title="Set as preferred"
                            >
                              Set Preferred
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteSupplier(ps.id)}
                            className="text-xs text-red-600 hover:text-red-800 font-medium"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Stock Control Settings */}
          {product.type === 'Product' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Stock Control</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reorder Level</label>
                  <input
                    type="number"
                    value={product.reorder_level}
                    onChange={(e) => setProduct(prev => ({ ...prev, reorder_level: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Alert when stock falls below this</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reorder Quantity</label>
                  <input
                    type="number"
                    value={product.reorder_quantity}
                    onChange={(e) => setProduct(prev => ({ ...prev, reorder_quantity: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Suggested order quantity</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar - 1/3 width */}
        <div className="col-span-1 space-y-6">
          
          {/* Photo */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Product Photo</h2>
            <div className="space-y-4">
              {photoPreview && (
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                  <img 
                    src={photoPreview} 
                    alt="Product" 
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
              {!photoPreview && (
                <div className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
                  <span className="text-4xl text-gray-400">📦</span>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <p className="text-xs text-gray-500">JPG, PNG, or GIF. Max 5MB.</p>
            </div>
          </div>

          {/* Stock Summary */}
          {!isNew && product.type === 'Product' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Stock Summary</h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center pb-3 border-b">
                  <span className="text-sm text-gray-600">Current Stock</span>
                  <span className="text-lg font-semibold text-gray-900">{product.current_stock || 0}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b">
                  <span className="text-sm text-gray-600">Reserved</span>
                  <span className="text-sm font-medium text-orange-600">{product.reserved_stock || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Available</span>
                  <span className="text-sm font-medium text-green-600">{product.available_stock || 0}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Supplier Modal */}
      {showAddSupplierModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl">
            <h2 className="text-2xl font-bold mb-4">Add Supplier</h2>
            
            <form onSubmit={handleAddSupplier}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Supplier *</label>
                  <select
                    value={newSupplier.supplier_id}
                    onChange={(e) => setNewSupplier({...newSupplier, supplier_id: e.target.value})}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select supplier...</option>
                    {suppliers.filter(s => !productSuppliers.find(ps => ps.supplier_id === s.id)).map(s => (
                      <option key={s.id} value={s.id}>{s.supplier_name}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Supplier SKU/Part Number</label>
                    <input
                      type="text"
                      value={newSupplier.supplier_sku}
                      onChange={(e) => setNewSupplier({...newSupplier, supplier_sku: e.target.value})}
                      placeholder="Supplier's product code"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cost Price (£)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newSupplier.cost_price}
                      onChange={(e) => setNewSupplier({...newSupplier, cost_price: e.target.value})}
                      placeholder="0.00"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Lead Time (days)</label>
                    <input
                      type="number"
                      value={newSupplier.lead_time_days}
                      onChange={(e) => setNewSupplier({...newSupplier, lead_time_days: e.target.value})}
                      placeholder="e.g., 7"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex items-center pt-6">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newSupplier.is_preferred}
                        onChange={(e) => setNewSupplier({...newSupplier, is_preferred: e.target.checked})}
                        className="mr-2 h-4 w-4 text-blue-600 rounded"
                      />
                      <span className="text-sm text-gray-700">Set as preferred supplier</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={newSupplier.notes}
                    onChange={(e) => setNewSupplier({...newSupplier, notes: e.target.value})}
                    rows={3}
                    placeholder="Additional information..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                >
                  Add Supplier
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddSupplierModal(false)}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

export default ProductDetail;
